import {
  Body2,
  Button,
  mergeClasses,
  Persona,
  Skeleton,
  SkeletonItem,
  Spinner,
  Textarea,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowClockwise20Regular,
  ArrowRight16Regular,
  Checkmark20Regular,
  ChevronLeftRegular,
  Copy20Regular,
  ArrowUpload16Regular,
  DocumentFolder20Regular,
  Sparkle20Filled,
} from "@fluentui/react-icons";
import {
  addMessageToConversation,
  createConversation,
  getAccessLLMModels,
  getAIResponse,
  getAITitle,
  getCategories,
  getConversation,
  getMessagesOfConversation,
  getPrompts,
  getUseCasePrompt,
  getWorkspaceLibraryDocuments,
  updateConversationTitle,
} from "api";
import { ALLOWED_FILE_EXTENSIONS, ConstantValues } from "common/constants";
import { getChunkAndUpdateAIMessage } from "common/streamProcessing.js";
import { AIModelSettings } from "components/aiModelSettings/AIModelSettings";
import { AddPrompt } from "components/chat/addPrompt/AddPrompt";
import { WorkspaceManageContent } from "../WorkspaceManageContent/WorkspaceManageContent";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { ICategory } from "models/ICategory";
import { IAccessLLMModel } from "models/IAccessLLMModel";
import { ICitation } from "models/ICitation";
import { IConversation } from "models/IConversation";
import { IFile } from "models/IFile";
import { ITag } from "models/ITag";
import { IMessage } from "models/IMessage";
import { IStandardChartData } from "models/IChartData";
import { IAIResponseError } from "models/IResponseError";
import { IUser } from "models/IUser";
import { LLMType } from "models/enums/LLMType";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { showToast } from "redux/toastActions";
import { setAIModels } from "redux/accessLLMModelsSlice";
import { setCurrentPrompt, resetCurrentPrompt } from "redux/selectedPrompt";
import {
  clearLibraryDocumentsForKey,
  setLibraryDocumentsForKey,
} from "redux/workspaceLibraryDocuments";
import { ILLMConfig } from "redux/llmConfig";
import { RootState } from "store";
import { useSharedButtonStyles, useSharedTextStyles } from "theme/sharedStyles";
import { uploadFilesToStorage } from "utils/blobUtils";
import {
  copyRichText,
  copyTextToClipboard,
  renderContentWithCitations,
  stripHtmlTags,
  stripMarkdown,
} from "utils/generalUtils";
import { v4 as uuidv4 } from "uuid";
import { WorkspaceConversations } from "../WorkspaceConversations/WorkspaceConversations";
import { useStaticStyles, useStyles } from "./WorkspaceChat.styles";
import { IWorkspaceChatProps } from "./WorkspaceChat.types";

const PAGE_KEY = "project-iq-chat";
const WORKSPACE_ASSISTANT_NAME = "Workspace Assistant";
const VALID_ASSISTANT_PERSONA_NAMES = new Set([WORKSPACE_ASSISTANT_NAME]);

export const WorkspaceChat = ({
  workspaceId,
  workspaceName,
  workspaceCode,
  onConversationOpenChange,
}: IWorkspaceChatProps): JSX.Element => {
  useStaticStyles();
  const styles = useStyles();
  const sharedButtonStyles = useSharedButtonStyles();
  const sharedTextStyles = useSharedTextStyles();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentUser: IUser = useSelector(
    (state: RootState) => state.currentUserSlice.value,
  );
  const aiModels: IAccessLLMModel[] = useSelector(
    (state: RootState) => state.accessLLMModelsSlice.value,
  );
  const currentLLMConfig = useSelector(
    (state: RootState) =>
      state.currentLLMConfigSlice.config[PAGE_KEY] ?? {
        llmType: LLMType.CLAUDE,
        rag: false,
        citate: false,
        sourceRagCategories: [],
      },
  );
  const currentPrompt = useSelector(
    (state: RootState) => state.currentPromptSlice.prompts[ConstantValues.CHAT],
  ) as { id: string; description: string; title?: string } | null;
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string | null>(
    null,
  );
  const contentSourceKey = conversationId ?? ConstantValues.CHAT;

  const currentLibraryDocuments: IWorkspaceLibraryDocument[] = useSelector(
    (state: RootState) =>
      state.workspaceLibraryDocumentsSlice.documents[contentSourceKey] || [],
  );

  const [messages, setMessages] = useState<IMessage[]>([]);
  const [text, setText] = useState<string>(ConstantValues.EMPTY_STRING);
  const [aiGenerateMessage, setAiGenerateMessage] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const copiedIndexTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [isManageContentOpen, setIsManageContentOpen] = useState(false);
  const [allKnowledgeComponents, setAllKnowledgeComponents] = useState<ITag[]>(
    [],
  );
  const [allWorkspaceDocuments, setAllWorkspaceDocuments] = useState<
    IWorkspaceLibraryDocument[]
  >([]);
  const [isLoadingKnowledgeComponents, setIsLoadingKnowledgeComponents] =
    useState(false);
  const [isLoadingWorkspaceDocuments, setIsLoadingWorkspaceDocuments] =
    useState(false);
  const [chatUploadedFiles, setChatUploadedFiles] = useState<IFile[]>([]);
  const [focusedKnowledgeComponents, setFocusedKnowledgeComponents] = useState<
    ITag[]
  >([]);
  const [isRestoringContentSources, setIsRestoringContentSources] =
    useState(false);
  const chatFileInputRef = useRef<HTMLInputElement>(null);
  const pendingContentSourceRestoreRef = useRef<{
    conversationId: string;
    workspaceDocumentFilenames: string[];
    knowledgeComponentIds: string[];
    isWorkspaceDocumentsApplied: boolean;
    isKnowledgeComponentsApplied: boolean;
  } | null>(null);
  const hasLoadedWorkspaceDocumentsRef = useRef(false);
  const hasLoadedKnowledgeComponentsRef = useRef(false);
  const selectConversationRequestIdRef = useRef(0);
  const requestIdAtSendRef = useRef<number>(0);
  const justLoadedConversationRef = useRef<boolean>(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pendingScrollRef = useRef(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const prevWorkspaceIdRef = useRef<string>(workspaceId);
  const messageContentRefs = useRef<Record<number, HTMLElement | null>>({});

  useEffect(() => {
    return () => {
      if (copiedIndexTimeoutRef.current) {
        clearTimeout(copiedIndexTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (currentPrompt?.description) {
      setText(currentPrompt.description);
    }
  }, [currentPrompt?.id, currentPrompt?.description]);

  useEffect(() => {
    const promptId = searchParams.get("promptId");
    if (!promptId) return;

    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("promptId");
      next.delete("useCaseId");
      return next;
    });

    getUseCasePrompt({ promptId })
      .then((useCasePrompt) => {
        dispatch(
          setCurrentPrompt({
            key: ConstantValues.CHAT,
            prompt: {
              id: promptId,
              description: stripHtmlTags(useCasePrompt.prompt),
              title: useCasePrompt.title,
            },
          }),
        );
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (aiModels.length === 0) {
      getAccessLLMModels().then((data: IAccessLLMModel[]) => {
        dispatch(setAIModels(data));
      });
    }
  }, [aiModels.length, dispatch]);

  const resetToWelcomeState = useCallback(() => {
    justLoadedConversationRef.current = true;
    setConversationId(null);
    setConversationTitle(null);
    setMessages([]);
    messageContentRefs.current = {};
    setText(ConstantValues.EMPTY_STRING);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);
    setChatUploadedFiles([]);
    setFocusedKnowledgeComponents([]);
    dispatch(clearLibraryDocumentsForKey(ConstantValues.CHAT));
    pendingContentSourceRestoreRef.current = null;
    setIsRestoringContentSources(false);
    selectConversationRequestIdRef.current += 1;
    queryClient.invalidateQueries({
      queryKey: ["workspace-conversations", workspaceId],
    });
  }, [dispatch, queryClient, workspaceId]);

  useEffect(() => {
    if (prevWorkspaceIdRef.current !== workspaceId) {
      resetToWelcomeState();
      prevWorkspaceIdRef.current = workspaceId;
    }
  }, [workspaceId, resetToWelcomeState]);

  useEffect(() => {
    if (pendingScrollRef.current && messagesEndRef.current) {
      pendingScrollRef.current = false;
      const behavior = justLoadedConversationRef.current ? "auto" : "smooth";
      messagesEndRef.current.scrollIntoView({ behavior });
      justLoadedConversationRef.current = false;
    }
  }, [messages]);

  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "0";
      ta.style.height = `${ta.scrollHeight}px`;
    }
  }, [text]);

  const contentSourceCount = useMemo(
    () =>
      currentLibraryDocuments.length +
      chatUploadedFiles.length +
      focusedKnowledgeComponents.length,
    [currentLibraryDocuments, chatUploadedFiles, focusedKnowledgeComponents],
  );

  const { data: prompts } = useQuery("prompts", () => getPrompts(), {
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const fetchKnowledgeComponents = async () => {
      setIsLoadingKnowledgeComponents(true);
      try {
        const categories = await getCategories();
        const allTags: ITag[] = [];
        categories?.forEach((category: ICategory) => {
          if (category.tags) {
            category.tags.forEach((tag: ITag) => {
              allTags.push({ ...tag, category });
            });
          }
        });
        setAllKnowledgeComponents(
          allTags.filter((tag) => tag.isPublic === true),
        );
      } catch (error) {
        console.error("Failed to load knowledge components:", error);
      } finally {
        setIsLoadingKnowledgeComponents(false);
        hasLoadedKnowledgeComponentsRef.current = true;
      }
    };
    fetchKnowledgeComponents();
  }, []);

  const fetchWorkspaceDocuments = useCallback(async () => {
    if (!workspaceId) return;
    setIsLoadingWorkspaceDocuments(true);
    try {
      const docs = await getWorkspaceLibraryDocuments({ workspaceId });
      setAllWorkspaceDocuments(docs || []);
    } catch (error) {
      console.error("Failed to load workspace documents:", error);
    } finally {
      setIsLoadingWorkspaceDocuments(false);
      hasLoadedWorkspaceDocumentsRef.current = true;
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchWorkspaceDocuments();
  }, [fetchWorkspaceDocuments]);

  const finalizeContentSourceRestoreIfComplete = (
    pendingRestore: NonNullable<typeof pendingContentSourceRestoreRef.current>,
  ) => {
    if (
      pendingRestore.isWorkspaceDocumentsApplied &&
      pendingRestore.isKnowledgeComponentsApplied
    ) {
      pendingContentSourceRestoreRef.current = null;
      setIsRestoringContentSources(false);
    }
  };

  useEffect(() => {
    const pendingRestore = pendingContentSourceRestoreRef.current;
    if (
      !pendingRestore ||
      pendingRestore.conversationId !== conversationId ||
      pendingRestore.isWorkspaceDocumentsApplied ||
      isLoadingWorkspaceDocuments ||
      !hasLoadedWorkspaceDocumentsRef.current
    ) {
      return;
    }

    const matchedWorkspaceDocuments = allWorkspaceDocuments.filter((doc) =>
      pendingRestore.workspaceDocumentFilenames.includes(doc.filename),
    );

    dispatch(
      setLibraryDocumentsForKey({
        key: pendingRestore.conversationId,
        documents: matchedWorkspaceDocuments,
      }),
    );
    pendingRestore.isWorkspaceDocumentsApplied = true;
    finalizeContentSourceRestoreIfComplete(pendingRestore);
  }, [
    allWorkspaceDocuments,
    conversationId,
    dispatch,
    isLoadingWorkspaceDocuments,
  ]);

  useEffect(() => {
    const pendingRestore = pendingContentSourceRestoreRef.current;
    if (
      !pendingRestore ||
      pendingRestore.conversationId !== conversationId ||
      pendingRestore.isKnowledgeComponentsApplied ||
      isLoadingKnowledgeComponents ||
      !hasLoadedKnowledgeComponentsRef.current
    ) {
      return;
    }

    const matchedKnowledgeComponents = allKnowledgeComponents.filter(
      (component) =>
        component.id !== undefined &&
        pendingRestore.knowledgeComponentIds.includes(component.id),
    );

    setFocusedKnowledgeComponents(matchedKnowledgeComponents);
    pendingRestore.isKnowledgeComponentsApplied = true;
    finalizeContentSourceRestoreIfComplete(pendingRestore);
  }, [allKnowledgeComponents, conversationId, isLoadingKnowledgeComponents]);

  const buildLlmConfig = (): ILLMConfig & { defaultKnowledgeFile: string } => ({
    ...currentLLMConfig,
    defaultKnowledgeFile: `workspace_library/${workspaceId}/vectorstore.tar.gz`,
  });

  const buildMessage = ({
    content,
    role,
    chartData,
    sourceDocuments,
    files,
    citations,
  }: {
    content: string;
    role: string;
    chartData?: IStandardChartData;
    sourceDocuments?: unknown[];
    files: IFile[];
    citations: ICitation[];
  }): IMessage => ({
    id: uuidv4(),
    content,
    citations,
    files,
    isHidden: false,
    chartData,
    role,
    tags: focusedKnowledgeComponents,
    sourceDocuments,
    llmConfig: buildLlmConfig(),
    createdAt: Math.floor(Date.now() / 1000),
    displayName:
      role === "assistant"
        ? WORKSPACE_ASSISTANT_NAME
        : currentUser?.displayName,
  });

  const chatWithAi = async () => {
    if (!text || !text.trim()) {
      setText(ConstantValues.EMPTY_STRING);
      return;
    }

    const requestIdAtSend = selectConversationRequestIdRef.current;
    requestIdAtSendRef.current = requestIdAtSend;

    dispatch(resetCurrentPrompt({ key: ConstantValues.CHAT }));
    setIsStreaming(true);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    let activeConversationId = conversationId;

    if (!activeConversationId) {
      const created = await createConversation({
        conversation: {
          title: "New Chat",
          pinned: false,
          messages: [],
          workspaceId,
        },
      });
      activeConversationId = created.id;
      if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
      setConversationId(created.id);
      setConversationTitle("New Chat");

      dispatch(
        setLibraryDocumentsForKey({
          key: created.id,
          documents: currentLibraryDocuments,
        }),
      );
      dispatch(clearLibraryDocumentsForKey(ConstantValues.CHAT));
    }

    const llmConfig = buildLlmConfig();

    const optimisticUserMessage = buildMessage({
      content: text,
      role: "user",
      files: [],
      citations: [],
    });

    const newMessages = [...messages, optimisticUserMessage];
    pendingScrollRef.current = true;
    setMessages(newMessages);
    setText(ConstantValues.EMPTY_STRING);

    const persistedUserMessage = await addMessageToConversation({
      conversationId: activeConversationId,
      message: {
        content: text,
        files: chatUploadedFiles,
        isHidden: false,
        role: "user",
        tags: focusedKnowledgeComponents,
        selectedLibraryDocuments: currentLibraryDocuments,
        llmConfig,
        displayName: currentUser?.displayName,
      },
    });

    if (selectConversationRequestIdRef.current !== requestIdAtSend) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === optimisticUserMessage.id ? persistedUserMessage : m,
      ),
    );

    const messagesForAi = newMessages.map((m) =>
      m.id === optimisticUserMessage.id ? persistedUserMessage : m,
    );

    const inFocusFiles: string[] = [
      ...currentLibraryDocuments.map(
        (doc) => `workspace_library/${workspaceId}/${doc.filename}`,
      ),
      ...chatUploadedFiles.flatMap((f) =>
        f.url ? [f.url] : [`workspace_library/${workspaceId}/${f.filename}`],
      ),
      ...focusedKnowledgeComponents.flatMap(
        (kc) => kc.attachments?.flatMap((a) => (a.url ? [a.url] : [])) ?? [],
      ),
    ];

    const response = await (
      getAIResponse as (...args: unknown[]) => Promise<unknown>
    )({
      llmConfig,
      messages: messagesForAi,
      persistFiles: false,
      workspaceId,
      inFocusFiles: inFocusFiles.length ? inFocusFiles : undefined,
      conversationId: activeConversationId,
    });

    if ("error" in (response as Record<string, unknown>)) {
      if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
      setIsStreaming(false);
      throw new Error(
        `AI Response Error: ${(response as any).status || "Unknown"} - ${(response as any).message || "Something went wrong"}`,
      );
    }

    const { content, chartData, sourceDocuments, citations } =
      await getChunkAndUpdateAIMessage(
        response,
        (response as Response).body,
        null,
        setAiGenerateMessage as any,
        null,
        dispatch,
      );

    if (!content) {
      if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      return;
    }

    const optimisticAiMessage = buildMessage({
      content,
      role: "assistant",
      chartData,
      sourceDocuments,
      files: [],
      citations: citations ?? [],
    });

    if (selectConversationRequestIdRef.current !== requestIdAtSend) return;

    setMessages((prev) => [...prev, optimisticAiMessage]);
    setIsStreaming(false);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    const persistedAiMessage = await addMessageToConversation({
      conversationId: activeConversationId,
      message: {
        content,
        files: [],
        isHidden: false,
        chartData,
        role: "assistant",
        tags: focusedKnowledgeComponents,
        sourceDocuments,
        citations: citations ?? [],
        llmConfig,
        displayName: WORKSPACE_ASSISTANT_NAME,
      },
    });

    if (selectConversationRequestIdRef.current !== requestIdAtSend) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === optimisticAiMessage.id ? persistedAiMessage : m,
      ),
    );

    if (messages.length === 0) {
      const generatedTitle = await getAITitle({
        llmConfig,
        messages: [persistedUserMessage, persistedAiMessage],
      });
      const sanitizedTitle: string = (generatedTitle?.title ?? "")
        .replace(/^"|"$/g, ConstantValues.EMPTY_STRING)
        .trim();
      if (sanitizedTitle) {
        await updateConversationTitle({
          conversation: { id: activeConversationId },
          title: sanitizedTitle,
        });
        if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
        setConversationTitle(sanitizedTitle);
      }
    }

    queryClient.invalidateQueries({ queryKey: ["workspace-conversations"] });
    return response;
  };

  const chatMutation = useMutation({
    mutationFn: chatWithAi,
    onSuccess: () => {
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      setText(ConstantValues.EMPTY_STRING);
      setIsStreaming(false);
    },
    onError: (error: IAIResponseError) => {
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      if (selectConversationRequestIdRef.current !== requestIdAtSendRef.current)
        return;
      const errorMessage =
        error?.message || "Something went wrong. Please try again later.";
      const errorContent = `**Error:** ${errorMessage}`;
      const errorMsg = buildMessage({
        content: errorContent,
        role: "assistant",
        files: [],
        citations: [],
      });
      setMessages((prev) => [...prev, errorMsg]);
    },
  });

  const handleContinueInterruptedMessage = async (
    interruptedMessage: IMessage,
  ) => {
    if (isStreaming) return;
    if (!conversationId || !interruptedMessage.id) return;

    const requestIdAtSend = selectConversationRequestIdRef.current;
    requestIdAtSendRef.current = requestIdAtSend;

    setIsStreaming(true);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    const llmConfig = buildLlmConfig();

    const continuationPrompt =
      "Your previous response was cut off. Continue it starting from the exact " +
      "word or character where it stopped. Do not restart, summarize, repeat any " +
      "part of it, or add a new introduction, heading, or preamble - your reply " +
      "will be appended directly after the cut-off text.";

    const interruptedMessageIndex = messages.findIndex(
      (m) => m.id === interruptedMessage.id,
    );
    const truncatedMessages = messages.slice(0, interruptedMessageIndex + 1);

    const continuationPromptMessage = buildMessage({
      content: continuationPrompt,
      role: "user",
      files: [],
      citations: [],
    });

    const messagesForAi = [...truncatedMessages, continuationPromptMessage];

    const inFocusFiles: string[] = [
      ...currentLibraryDocuments.map(
        (doc) => `workspace_library/${workspaceId}/${doc.filename}`,
      ),
      ...chatUploadedFiles.flatMap((f) =>
        f.url ? [f.url] : [`workspace_library/${workspaceId}/${f.filename}`],
      ),
      ...focusedKnowledgeComponents.flatMap(
        (kc) => kc.attachments?.flatMap((a) => (a.url ? [a.url] : [])) ?? [],
      ),
    ];

    const response = await (
      getAIResponse as (...args: unknown[]) => Promise<unknown>
    )({
      llmConfig,
      messages: messagesForAi,
      persistFiles: false,
      workspaceId,
      inFocusFiles: inFocusFiles.length ? inFocusFiles : undefined,
      conversationId,
    });

    if ("error" in (response as Record<string, unknown>)) {
      if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
      setIsStreaming(false);
      const errorMessage =
        (response as any).message || "Failed to continue response";
      const errorContent = `**Error:** ${errorMessage}`;
      const errorMsg = buildMessage({
        content: errorContent,
        role: "assistant",
        files: [],
        citations: [],
      });
      setMessages((prev) => [...prev, errorMsg]);
      return;
    }

    const { content, chartData, sourceDocuments, citations } =
      await getChunkAndUpdateAIMessage(
        response,
        (response as Response).body,
        null,
        setAiGenerateMessage as any,
        null,
        dispatch,
      );

    if (!content) {
      if (selectConversationRequestIdRef.current !== requestIdAtSend) return;
      setIsStreaming(false);
      setAiGenerateMessage(ConstantValues.EMPTY_STRING);
      return;
    }

    const continuationMessage = buildMessage({
      content,
      role: "assistant",
      chartData,
      sourceDocuments,
      files: [],
      citations: citations ?? [],
    });

    if (selectConversationRequestIdRef.current !== requestIdAtSend) return;

    setMessages((prev) => [...prev, continuationMessage]);
    setIsStreaming(false);
    setAiGenerateMessage(ConstantValues.EMPTY_STRING);

    const persistedContinuationMessage = await addMessageToConversation({
      conversationId,
      message: {
        content,
        files: [],
        isHidden: false,
        chartData,
        role: "assistant",
        tags: focusedKnowledgeComponents,
        sourceDocuments,
        citations: citations ?? [],
        llmConfig,
        displayName: WORKSPACE_ASSISTANT_NAME,
      },
    });

    if (selectConversationRequestIdRef.current !== requestIdAtSend) return;

    setMessages((prev) =>
      prev.map((m) =>
        m.id === continuationMessage.id ? persistedContinuationMessage : m,
      ),
    );

    queryClient.invalidateQueries({ queryKey: ["workspace-conversations"] });
  };

  const handleCopyToClipboard = async (content: string, index: number) => {
    try {
      const messageElement = messageContentRefs.current[index];
      if (messageElement) {
        await copyRichText(messageElement, stripMarkdown(content));
      } else {
        await copyTextToClipboard(stripMarkdown(content));
      }
      setCopiedIndex(index);
      if (copiedIndexTimeoutRef.current) {
        clearTimeout(copiedIndexTimeoutRef.current);
      }
      copiedIndexTimeoutRef.current = setTimeout(
        () => setCopiedIndex(null),
        2000,
      );
    } catch {
      setCopiedIndex(null);
    }
  };

  const onSend = () => chatMutation.mutate();

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (chatMutation.isLoading) return;
      chatMutation.mutate();
    }
  };

  const handleWorkspaceDocumentsChange = useCallback(
    (docs: IWorkspaceLibraryDocument[]) => {
      dispatch(
        setLibraryDocumentsForKey({ key: contentSourceKey, documents: docs }),
      );
    },
    [contentSourceKey, dispatch],
  );

  const handleFocusedKnowledgeComponentsChange = useCallback((tags: ITag[]) => {
    setFocusedKnowledgeComponents(tags);
  }, []);

  const handleUploadedDocumentsChange = useCallback((files: IFile[]) => {
    setChatUploadedFiles(files);
  }, []);

  const handleSelectConversation = useCallback(
    async (conversation: IConversation) => {
      if (!conversation.id) return;

      const requestId = ++selectConversationRequestIdRef.current;

      justLoadedConversationRef.current = true;
      setChatUploadedFiles([]);
      setFocusedKnowledgeComponents([]);
      setIsRestoringContentSources(true);
      if (conversationId === null) {
        dispatch(clearLibraryDocumentsForKey(ConstantValues.CHAT));
      }

      try {
        const [msgs, fetchedConversation]: [IMessage[], IConversation] =
          await Promise.all([
            getMessagesOfConversation({ conversationId: conversation.id }),
            getConversation({ conversationId: conversation.id }),
          ]);

        if (selectConversationRequestIdRef.current !== requestId) {
          return;
        }

        setConversationId(conversation.id);
        setConversationTitle(fetchedConversation.title);
        pendingScrollRef.current = true;
        setMessages(msgs);
        messageContentRefs.current = {};

        const workspaceDocumentFilenames =
          fetchedConversation.inFocusWorkspaceDocumentFilenames ?? [];
        const knowledgeComponentIds =
          fetchedConversation.inFocusKnowledgeComponentIds ?? [];
        const uploadedFiles = fetchedConversation.inFocusUploadedFiles ?? [];

        setChatUploadedFiles(uploadedFiles);
        pendingContentSourceRestoreRef.current = {
          conversationId: conversation.id,
          workspaceDocumentFilenames,
          knowledgeComponentIds,
          isWorkspaceDocumentsApplied: false,
          isKnowledgeComponentsApplied: false,
        };
      } catch {
        if (selectConversationRequestIdRef.current !== requestId) {
          return;
        }
        justLoadedConversationRef.current = false;
        setIsRestoringContentSources(false);
        dispatch(
          showToast("Failed to load conversation. Please try again.", "error"),
        );
      }
    },
    [conversationId, dispatch],
  );

  const handleChatFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (!files.length || !workspaceId) return;

    const allowedExtensions = ALLOWED_FILE_EXTENSIONS.map((e) =>
      e.toLowerCase(),
    );

    let nextUploadedFiles = chatUploadedFiles;
    let hasNewUploads = false;

    for (const file of files) {
      const ext = file.name.split(".").pop()?.toLowerCase();
      if (!ext || !allowedExtensions.includes(ext)) {
        dispatch(showToast(`File "${file.name}" is not supported.`, "warning"));
        continue;
      }
      if (nextUploadedFiles.some((f) => f.filename === file.name)) continue;
      try {
        const uploaded = await uploadFilesToStorage(
          [{ id: crypto.randomUUID(), file }],
          "workspace_library",
          workspaceId,
          false,
        );
        if (uploaded.length > 0) {
          const iFile: IFile = {
            id: uploaded[0].id,
            filename: file.name,
            url: uploaded[0].url ?? null,
          };
          nextUploadedFiles = [...nextUploadedFiles, iFile];
          hasNewUploads = true;
        }
      } catch {
        dispatch(
          showToast("Failed to upload file. Please try again.", "error"),
        );
      }
    }

    if (hasNewUploads) {
      handleUploadedDocumentsChange(nextUploadedFiles);
    }
    event.target.value = "";
  };

  const hasMessages = messages.length > 0;
  const isConversationOpen = hasMessages || isStreaming;

  useLayoutEffect(() => {
    onConversationOpenChange?.(isConversationOpen);
  }, [isConversationOpen, onConversationOpenChange]);

  const isSendDisabled = !text || text.trim() === "" || isStreaming;

  // The composer is rendered in a different tree position once a conversation
  // opens, so the textarea remounts on the first message. Refocus the new one.
  useEffect(() => {
    if (isConversationOpen && chatMutation.isLoading) {
      textareaRef.current?.focus();
    }
  }, [isConversationOpen]);
  const displayCode = workspaceCode
    ? `${workspaceName} · ${workspaceCode}`
    : workspaceName;
  const greeting = currentUser?.displayName
    ? `Good evening, ${currentUser.displayName.split(" ")[0]}.`
    : "";

  const composer = (
    <div className={styles.inputCard}>
      <Textarea
        id={PAGE_KEY}
        placeholder={`Ask anything about ${workspaceName} — drafting, analysis, synthesis...`}
        resize="none"
        ref={textareaRef}
        className={`piq-chat-textarea ${styles.textArea}`}
        aria-label="Chat message"
        textarea={{
          className: mergeClasses(
            styles.innerTextarea,
            chatMutation.isLoading && styles.innerTextareaBusy,
          ),
        }}
        value={text}
        onChange={(_e, data) => setText(data.value)}
        readOnly={chatMutation.isLoading}
        aria-disabled={chatMutation.isLoading || undefined}
        onKeyDown={onEnterPress}
      />

      <div className={styles.toolbarRow}>
        <div>
          <input
            ref={chatFileInputRef}
            type="file"
            hidden
            accept={ALLOWED_FILE_EXTENSIONS.map(
              (e) => `.${e.toLowerCase()}`,
            ).join(",")}
            onChange={handleChatFileSelect}
            multiple
          />
          <Tooltip content="Upload a file to this chat" relationship="label">
            <Button
              className={mergeClasses(
                sharedButtonStyles.ghostButton,
                styles.contentSourceButtonReset,
                styles.contentSourceIcon,
              )}
              icon={<ArrowUpload16Regular aria-hidden="true" />}
              onClick={() => chatFileInputRef.current?.click()}
              aria-label="Upload a file to this chat"
            />
          </Tooltip>
        </div>
        <div className={styles.attachButton}>
          <Tooltip content="Content Sources" relationship="label">
            <div className={styles.contentSourceButtonWrapper}>
              <Button
                className={mergeClasses(
                  sharedButtonStyles.ghostButton,
                  styles.contentSourceButtonReset,
                  styles.contentSourceIcon,
                )}
                icon={
                  isRestoringContentSources ? (
                    <Spinner size="tiny" aria-hidden="true" />
                  ) : (
                    <DocumentFolder20Regular aria-hidden="true" />
                  )
                }
                onClick={() => setIsManageContentOpen(true)}
                aria-busy={isRestoringContentSources}
                aria-label={
                  isRestoringContentSources
                    ? "Manage content sources, loading"
                    : `Manage content sources${contentSourceCount > 0 ? `, ${contentSourceCount} selected` : ""}`
                }
              />
              {!isRestoringContentSources && contentSourceCount > 0 && (
                <span className={styles.contentSourceBadge} aria-hidden="true">
                  {contentSourceCount}
                </span>
              )}
            </div>
          </Tooltip>
        </div>
        <div className={styles.promptButtonWrapper}>
          <AddPrompt
            dictKey={ConstantValues.CHAT}
            canManagePrompt={true}
            data={prompts}
            onKnowledgeComponentsSelected={setFocusedKnowledgeComponents}
          />
        </div>
        <AIModelSettings pageName={PAGE_KEY} variant="compact" />

        <button
          disabled={isSendDisabled}
          onClick={onSend}
          className={mergeClasses(
            styles.sendButton,
            isSendDisabled && styles.sendButtonDisabled,
          )}
          aria-label="Send message"
          title="Send message"
        >
          <ArrowRight16Regular />
        </button>
      </div>
    </div>
  );

  return (
    <div className={styles.root}>
      {!hasMessages && !isStreaming ? (
        <>
          <div className={styles.welcomeArea}>
            <div className={styles.welcomeBadge}>
              <Sparkle20Filled
                className={mergeClasses(
                  styles.welcomeBadgeIcon,
                  /claude|anthropic|haiku|sonnet|opus/i.test(
                    currentLLMConfig.llmType,
                  ) && styles.welcomeBadgeIconClaude,
                  /openai|gpt/i.test(currentLLMConfig.llmType) &&
                    styles.welcomeBadgeIconOpenai,
                )}
              />
              <Text>Workspace assistant · {displayCode}</Text>
            </div>
            <Text className={styles.welcomeTitle}>
              What can I do for you today?
            </Text>
            <Text className={styles.welcomeSubtitle}>
              {greeting} I can read every file and capability in{" "}
              <strong>{workspaceName}</strong>&apos;s shared context.
            </Text>
          </div>

          <div className={styles.messageBarArea}>{composer}</div>

          <WorkspaceConversations
            workspaceId={workspaceId}
            workspaceName={workspaceName}
            onSelectConversation={handleSelectConversation}
          />
        </>
      ) : (
        <>
          <nav aria-label="Breadcrumb" className={styles.chatBreadcrumbBar}>
            <Tooltip
              content={
                isStreaming ? "Wait for response to complete" : "Back to chats"
              }
              relationship="label"
            >
              <button
                className={mergeClasses(
                  styles.chatBackButton,
                  isStreaming && styles.chatBackButtonDisabled,
                )}
                aria-disabled={isStreaming}
                onClick={() => {
                  if (isStreaming) return;
                  resetToWelcomeState();
                }}
              >
                <ChevronLeftRegular aria-hidden="true" />
                Chats
              </button>
            </Tooltip>
            <span className={styles.chatBreadcrumbSeparator} aria-hidden="true">
              /
            </span>
            <span className={styles.chatBreadcrumbTitle} aria-current="page">
              {conversationTitle ?? "New Chat"}
            </span>
          </nav>
          <div ref={messagesContainerRef} className={styles.messagesArea}>
            <div className={styles.messagesContainer}>
              {messages.map((message, index) => {
                const assistantDisplayName =
                  message.displayName &&
                  VALID_ASSISTANT_PERSONA_NAMES.has(message.displayName)
                    ? message.displayName
                    : WORKSPACE_ASSISTANT_NAME;
                return (
                  <div
                    key={index}
                    className={mergeClasses(
                      styles.messageRow,
                      message.role === "assistant"
                        ? styles.assistantMessage
                        : styles.userMessage,
                    )}
                  >
                    <Persona
                      name={
                        message.role === "assistant"
                          ? assistantDisplayName
                          : (message.displayName ?? currentUser?.displayName)
                      }
                      secondaryText={message.role}
                      size="large"
                      avatar={
                        message.role === "assistant"
                          ? { image: { src: "/images/NH_Logo_Circle.png" } }
                          : {
                              name:
                                message.displayName ?? currentUser?.displayName,
                            }
                      }
                    />
                    <div
                      ref={(el) => {
                        messageContentRefs.current[index] = el;
                      }}
                      className={styles.messageContentWrapper}
                    >
                      <div>
                        <Body2 className={styles.messageContent}>
                          {renderContentWithCitations(
                            message.content,
                            message.citations,
                          )}
                        </Body2>
                      </div>
                    </div>
                    {message.role === "assistant" && (
                      <div className={styles.messageActions}>
                        <Tooltip content="Copy message" relationship="label">
                          <button
                            className={styles.messageActionButton}
                            onClick={() =>
                              handleCopyToClipboard(message.content, index)
                            }
                            aria-label="Copy message"
                          >
                            {copiedIndex === index ? (
                              <Checkmark20Regular aria-hidden="true" />
                            ) : (
                              <Copy20Regular aria-hidden="true" />
                            )}
                          </button>
                        </Tooltip>
                        {message.status === "interrupted" && (
                          <>
                            <Text className={sharedTextStyles.caption}>
                              Response was interrupted
                            </Text>
                            <Tooltip
                              content="Continue generating"
                              relationship="label"
                            >
                              <button
                                onClick={() =>
                                  handleContinueInterruptedMessage(message)
                                }
                                disabled={isStreaming}
                                className={styles.messageActionButton}
                                aria-label="Continue generating response"
                              >
                                <ArrowClockwise20Regular aria-hidden="true" />
                              </button>
                            </Tooltip>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {isStreaming && (
                <div
                  className={mergeClasses(
                    styles.messageRow,
                    styles.assistantMessage,
                  )}
                >
                  <Persona
                    name={WORKSPACE_ASSISTANT_NAME}
                    secondaryText="assistant"
                    size="large"
                    avatar={{ image: { src: "/images/NH_Logo_Circle.png" } }}
                  />
                  {aiGenerateMessage && aiGenerateMessage.length > 0 ? (
                    <Body2 className={styles.messageContent}>
                      {renderContentWithCitations(aiGenerateMessage, [])}
                    </Body2>
                  ) : (
                    <Body2 className={styles.messageContent}>
                      <Skeleton aria-label="Loading response">
                        <div className={styles.skeletonContent}>
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            style={{ width: "80%" }}
                          />
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            style={{ width: "60%" }}
                          />
                          <SkeletonItem
                            animation="wave"
                            size={8}
                            style={{ width: "20%" }}
                          />
                        </div>
                      </Skeleton>
                    </Body2>
                  )}
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          </div>
        </>
      )}

      {(hasMessages || isStreaming) && (
        <div className={styles.messageBarArea}>{composer}</div>
      )}

      <WorkspaceManageContent
        isOpen={isManageContentOpen}
        isPersonalWorkspace={false}
        onClose={() => setIsManageContentOpen(false)}
        knowledgeComponents={allKnowledgeComponents}
        selectedKnowledgeComponents={focusedKnowledgeComponents}
        onKnowledgeComponentsChange={handleFocusedKnowledgeComponentsChange}
        workspaceDocuments={allWorkspaceDocuments}
        selectedWorkspaceDocuments={currentLibraryDocuments}
        onWorkspaceDocumentsChange={handleWorkspaceDocumentsChange}
        uploadedDocuments={chatUploadedFiles}
        onUploadedDocumentsChange={handleUploadedDocumentsChange}
        workspaceId={workspaceId}
        onWorkspaceDocumentsRefresh={fetchWorkspaceDocuments}
        isLoadingKnowledgeComponents={isLoadingKnowledgeComponents}
        isLoadingWorkspaceDocuments={isLoadingWorkspaceDocuments}
      />
    </div>
  );
};
